import type { Reference, StoreObject } from '@apollo/client'
import type { ModifierDetails } from '@apollo/client/cache'

import { graphql } from '~publish/graphql'
import { removeDraftFromDraftsLists } from '~publish/helpers/drafts/removeDraftFromDraftsLists'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'

import { useTypedMutation } from '../../hooks/useTypedMutation'

/**
 * Cache-edge shape for the `contentItems` connection: inside a `cache.modify`
 * field the edge nodes are store references, not the fetched entity.
 */
type ContentItemCacheEdge = { node: Reference | StoreObject }

export const DeleteContentItem = graphql(/* GraphQL */ `
  mutation DeleteContentItem($id: ContentItemId!) {
    deleteContentItem(input: { id: $id }) {
      __typename
      ... on EmptySuccess {
        _empty
      }
      ... on MutationError {
        message
      }
    }
  }
`)

/**
 * Deletes a channel-less draft's content item. A content item still holding a
 * channel-less draft has no posts, so nothing blocks its deletion; the failure
 * arm only applies once an item has been promoted to channel posts, which this
 * card never surfaces.
 *
 * The deleted edge is filtered out of every cached `contentItems` and `drafts`
 * connection so the drafts list updates without a refetch, mirroring
 * {@link useDeletePost}.
 * `EmptySuccess` carries no id, so removal keys off the mutation `variables.id`.
 *
 * The drafts count is refetched instead: it selects no edges, so the cache
 * update above has nothing to filter and cannot tell the total dropped by one.
 */
type UseDeleteContentItem = ReturnType<
  typeof useTypedMutation<
    'EmptySuccess',
    ResultOf<typeof DeleteContentItem>['deleteContentItem'],
    ResultOf<typeof DeleteContentItem>,
    VariablesOf<typeof DeleteContentItem>
  >
>

export const useDeleteContentItem = (): UseDeleteContentItem =>
  useTypedMutation(DeleteContentItem, (data) => data.deleteContentItem, {
    successTypename: 'EmptySuccess',
    refetchQueries: ['GetContentItemDraftsCount'],
    optimisticResponse: {
      deleteContentItem: {
        __typename: 'EmptySuccess' as const,
        _empty: '',
      },
    },
    update: (cache, { data }, { variables }) => {
      if (!isOfType(data?.deleteContentItem, 'EmptySuccess')) {
        return
      }

      const deletedId = variables?.id
      if (!deletedId) {
        return
      }

      cache.modify({
        fields: {
          contentItems(
            existingConnection: Reference | StoreObject,
            { readField }: ModifierDetails,
          ) {
            if (existingConnection && '__ref' in existingConnection) {
              return existingConnection
            }

            const edges =
              readField<ContentItemCacheEdge[]>('edges', existingConnection) ||
              []

            const remainingEdges = edges.filter(
              (edge) => readField('id', edge.node) !== deletedId,
            )
            if (remainingEdges.length === edges.length) {
              return existingConnection
            }

            const totalCount = readField<number>(
              'totalCount',
              existingConnection,
            )

            return {
              ...existingConnection,
              edges: remainingEdges,
              ...(typeof totalCount === 'number'
                ? { totalCount: Math.max(0, totalCount - 1) }
                : {}),
            }
          },
        },
      })
      removeDraftFromDraftsLists(cache, deletedId)

      cache.gc()
    },
  })
